import { FormikProvider, useFormik } from "formik";
import BrowserHeader from "../../../components/BrowserHeader/BrowserHeader";
import {
	CardBody,
	CardContainer,
	CardHeader,
} from "../../../components/DashboardCard/DashboardCard";
import DropdownField, {
	DropdownOptions,
} from "../../../components/FormFields/DropdownField";
import React, { useEffect, useState } from "react";
import Button, { SquareButton } from "../../../components/Button/Button";
import {
	CreditFieldTemplate,
	DateFieldTemplate,
	LinkFieldTemplate,
	ListFieldTemplate,
	MediaFieldTemplate,
	PhysicalComponentFieldTemplate,
	ProgramModel,
	ProgramCreditField,
	TextFieldTemplate,
	ProgramDateField,
	ProgramListField,
	ProgramTextField,
	ProgramLinkField,
	ProgramMediaField,
	ProgramPhysicalComponentField,
	ProgramDeadline,
	Ancestor,
} from "./ProgramInterfaces";
import {
	createCreditFieldTemplate,
	createDateFieldTemplate,
	createLinkFieldTemplate,
	createListFieldTemplate,
	createMediaFieldTemplate,
	createPhysicalComponentFieldTemplate,
	createTextFieldTemplate,
	FieldTemplateNames,
	getActiveCreditFieldTemplates,
	getActiveDateFieldTemplates,
	getActiveLinkFieldTemplates,
	getActiveListFieldTemplates,
	getActiveMediaFieldTemplates,
	getActivePhysicalComponentFieldTemplates,
	getActiveTextFieldTemplates,
	deleteProgramTextField,
	deleteProgramCreditField,
	deleteProgramDateField,
	deleteProgramListField,
	deleteProgramLinkField,
	deleteProgramMediaField,
	deleteProgramPhysicalComponentField,
} from "./manageFieldTemplates";
import styled, { useTheme, css } from "styled-components";
import { useAlert } from "../../../components/Alert/Alerts";
import TextField from "../../../components/FormFields/TextField";
import {
	getProgramById,
	getProgramConfigById,
	getRootProgramConfig,
	getRootProgramOptions,
} from "./manageProgram";
import {
	MediaCountIcon,
	ProgramConfigureChild,
	ProgramFieldCount,
} from "./ProgramConfig";
import Loading from "../../../components/Loading/Loading";
import Icon from "../../../components/Icon/Icon";
import { ModalCard } from "../../../components/Modal/Modal";
import CheckboxField, {
	FakeCheckbox,
	RegularCheckBox,
} from "../../../components/FormFields/CheckboxField";

const Circle = styled.div<{ color?: string }>`
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: 999px;
	border: solid 12px ${(p) => (p.color ? p.color : p.theme.colorPrimary)};
	width: 25px;
	height: 25px;
`;

export const Square = styled.div<{
	background: string;
	border: string;
}>`
	padding: 5px;
	color: ${({ theme }) => theme.colorCopyLightLight};
	background: ${(p) => p.background};
	border: ${(p) => p.border};
`;

const ProgramLevelContainer = styled.div<{
	depth: number;
	hasChildren: boolean;
}>`
	display: flex;
	align-items: center;
	gap: 1rem;
	height: 112px;
	padding: 15px 19px;
	margin-left: ${(p) => p.depth * 1.5}rem;
	background: ${({ theme }) => theme.colorBackgroundLight};
	${(p) =>
		p.hasChildren &&
		css`
			cursor: pointer;
		`};
`;

const isProgramFieldCount = (obj: any): obj is ProgramFieldCount => {
	return "children" in obj && Array.isArray(obj.children);
};

function sortProgramsByHierarchy(programs: SelectProgram[]): SelectProgram[] {
	return [...programs].sort((a, b) => {
		const pathA = [...a.ancestry.map((a) => a.id), a.id];
		const pathB = [...b.ancestry.map((a) => a.id), b.id];

		const len = Math.max(pathA.length, pathB.length);
		for (let i = 0; i < len; i++) {
			const valA = pathA[i] ?? -1;
			const valB = pathB[i] ?? -1;

			if (valA !== valB) return valA - valB;
		}
		return 0;
	});
}

const ProgramLevel = (props: {
	className?: string;
	program: ProgramFieldCount | ProgramConfigureChild;
	handleExpand?(): void;
	programsToAdd: SelectProgram[];
	checked: boolean;
	toggleProgram(program: SelectProgram): void;
}) => {
	const theme = useTheme();
	const {
		name,
		id,
		depth,
		hasChildren,
		ancestry,
		isEntryLevel,
		isExecution,
		hasDeadlinePricing,
	} = props.program;
	const [fetchedChildren, setFetchedChildren] = useState<
		ProgramConfigureChild[]
	>([]);
	const [expanded, setExpanded] = useState(false);

	useEffect(() => {
		if (id !== null && id !== undefined) {
			if (
				isProgramFieldCount(props.program) &&
				props.program.children.length > 0
			) {
				setFetchedChildren(props.program.children);
			}
		}
	}, [props.program]);

	// const toggleProgram = (isChecked: boolean) => {
	// 	if (isChecked) {
	// 		props.addProgramToArr({ id: id, name: name });
	// 	} else {
	// 		props.removeProgramFromArr(id);
	// 	}
	// };

	const handleExpand = () => {
		// fetch clicked program's children
		if (props.handleExpand && !expanded) {
			getProgramConfigById(String(id)).then((res) => {
				if (res.status === 200) {
					setFetchedChildren(res.data.children);
				}
			});
		}
		setExpanded(!expanded);
	};

	return (
		<>
			<ProgramLevelContainer
				key={`programLevel-${id}`}
				depth={depth}
				hasChildren={hasChildren}
				onClick={handleExpand}
			>
				<FakeCheckbox
					key={`toggle-${id}`}
					checked={props.checked}
					onClick={(e) => {
						e.stopPropagation();
						props.toggleProgram({ id: id, name: name, ancestry: ancestry });
					}}
				/>

				<span>
					{name} ({id})
				</span>
				{isExecution && (
					<Square
						background={theme.colorPrimaryAccent}
						border={theme.colorPrimaryAccent}
					>
						XL
					</Square>
				)}

				{isEntryLevel && hasDeadlinePricing && (
					<Square
						background={theme.colorActivationDark}
						border={theme.colorActivationDark}
					>
						EL
					</Square>
				)}

				<div className="min-w-[30%] max-w-[50%] flex gap-[1rem] overflow-x-auto">
					{props.program.mediaCount?.map((count, i) => (
						<div key={i} className="flex flex-col items-center justify-end">
							<MediaCountIcon mediaType={count.mediaType} />
							<p className="min-w-[55px] text-center">Min: {count.range.min}</p>
							<p className="min-w-[55px] text-center">Max: {count.range.max}</p>
						</div>
					))}
				</div>

				{hasChildren && (
					<Icon
						className="ml-auto"
						icon="caret"
						color={theme.colorCopyLight}
						rotation={expanded ? undefined : "-90deg"}
					/>
				)}
			</ProgramLevelContainer>
			{expanded &&
				fetchedChildren?.length > 0 &&
				fetchedChildren.map((child) => (
					<ProgramLevel
						key={child.id}
						program={child}
						handleExpand={handleExpand}
						// addProgramToArr={props.addProgramToArr}
						// removeProgramFromArr={props.removeProgramFromArr}
						programsToAdd={props.programsToAdd}
						checked={props.programsToAdd.some((p) => p.id === child.id)}
						toggleProgram={props.toggleProgram}
					/>
				))}
		</>
	);
};

const BulkAddFieldTemplates = (props: BulkAddFieldTemplatesProps) => {
	const theme = useTheme();
	const { addNewAlert } = useAlert();

	const formikProps = useFormik({
		initialValues: {
			creditToAdd: [],
			dateToAdd: [],
			listToAdd: [],
			textToAdd: [],
			linkToAdd: [],
			mediaToAdd: [],
			physicalComponentToAdd: [],
		},
		onSubmit: async (values) => {},
	});

	// loading state
	const [addingField, setAddingField] = useState(false);
	const [programsToAdd, setProgramsToAdd] = useState<SelectProgram[]>([]);
	const [rootPrograms, setRootPrograms] = useState<DropdownOptions[]>([]);
	const [nestedProgram, setNestedProgram] = useState<ProgramFieldCount | null>(
		null
	);

	const sortedPrograms = sortProgramsByHierarchy(programsToAdd);

	const [activeCredits, setActiveCredits] = useState<CreditFieldTemplate[]>([]);
	const [activeDates, setActiveDates] = useState<DateFieldTemplate[]>([]);
	const [activeLists, setActiveLists] = useState<ListFieldTemplate[]>([]);
	const [activeTexts, setActiveTexts] = useState<TextFieldTemplate[]>([]);
	const [activeLinks, setActiveLinks] = useState<LinkFieldTemplate[]>([]);
	const [activeMedia, setActiveMedia] = useState<MediaFieldTemplate[]>([]);
	const [activePhysicalComponents, setActivePhysicalComponents] = useState<
		PhysicalComponentFieldTemplate[]
	>([]);

	const [fieldDropdownData, setFieldDropdownData] = useState(0);

	function handleFieldDropdownData(selectedOption: any) {
		if (selectedOption.target.value.toString() == "") {
			setFieldDropdownData(0);
		} else {
			setFieldDropdownData(parseInt(selectedOption.target.value));
		}
	}

	async function PostAddProgramField(programFieldToAdd: any) {
		try {
			let fieldToAdd: any;
			switch (fieldDropdownData) {
				case 1:
					const creditTemplate = activeCredits.find(
						(c) => c.id === programFieldToAdd.creditToAdd
					)!;
					const creditField: ProgramCreditField = {
						...creditTemplate,
						id: undefined,
						modifiedBy: undefined,
						modifiedDate: undefined,
						createdBy: undefined,
						createdDate: undefined,
						programId: programFieldToAdd.programId,
						templateId: programFieldToAdd.creditToAdd,
					};
					createCreditFieldTemplate(creditField).then((response) => {
						if (response.status == 201) {
							fieldToAdd = response.data;
						}
					});
					break;
				case 2:
					const dateTemplate = activeDates.find(
						(c) => c.id === programFieldToAdd.dateToAdd
					)!;
					const dateField: ProgramDateField = {
						...dateTemplate,
						id: undefined,
						modifiedBy: undefined,
						modifiedDate: undefined,
						createdBy: undefined,
						createdDate: undefined,
						programId: programFieldToAdd.programId,
						templateId: programFieldToAdd.dateToAdd,
					};
					createDateFieldTemplate(dateField).then((response) => {
						if (response.status == 201) {
							fieldToAdd = response.data;
						}
					});
					break;
				case 3:
					const listTemplate = activeLists.find(
						(c) => c.id === programFieldToAdd.listToAdd
					)!;
					const listField: ProgramListField = {
						...listTemplate,
						id: undefined,
						modifiedBy: undefined,
						modifiedDate: undefined,
						createdBy: undefined,
						createdDate: undefined,
						programId: programFieldToAdd.programId,
						templateId: programFieldToAdd.listToAdd,
					};
					//console.log("List Temlate: ", listTemplate);
					//console.log("List Field: ", listField);
					createListFieldTemplate(listField).then((response) => {
						if (response.status == 201) {
							fieldToAdd = response.data;
						}
					});
					break;
				case 4:
					const textTemplate = activeTexts.find(
						(c) => c.id === programFieldToAdd.textToAdd
					)!;
					const textField: ProgramTextField = {
						...textTemplate,
						id: undefined,
						modifiedBy: undefined,
						modifiedDate: undefined,
						createdBy: undefined,
						createdDate: undefined,
						programId: programFieldToAdd.programId,
						templateId: programFieldToAdd.textToAdd,
					};
					createTextFieldTemplate(textField).then((response) => {
						if (response.status == 201) {
							fieldToAdd = response.data;
						}
					});
					break;
				case 5:
					const linkTemplate = activeLinks.find(
						(c) => c.id === programFieldToAdd.linkToAdd
					)!;
					const linkField: ProgramLinkField = {
						...linkTemplate,
						id: undefined,
						modifiedBy: undefined,
						modifiedDate: undefined,
						createdBy: undefined,
						createdDate: undefined,
						programId: programFieldToAdd.programId,
						templateId: programFieldToAdd.linkToAdd,
					};
					createLinkFieldTemplate(linkField).then((response) => {
						if (response.status == 201) {
							fieldToAdd = response.data;
						}
					});
					break;
				case 6:
					const mediaTemplate = activeMedia.find(
						(c) => c.id === programFieldToAdd.mediaToAdd
					)!;
					const mediaField: ProgramMediaField = {
						...mediaTemplate,
						id: undefined,
						modifiedBy: undefined,
						modifiedDate: undefined,
						createdBy: undefined,
						createdDate: undefined,
						programId: programFieldToAdd.programId,
						templateId: programFieldToAdd.mediaToAdd,
					};
					createMediaFieldTemplate(mediaField).then((response) => {
						if (response.status == 201) {
							fieldToAdd = response.data;
						}
					});
					break;
				case 7:
					const physicalComponentTemplate = activePhysicalComponents.find(
						(c) => c.id === programFieldToAdd.physicalComponentToAdd
					)!;
					const physicalComponentField: ProgramPhysicalComponentField = {
						...physicalComponentTemplate,
						id: undefined,
						modifiedBy: undefined,
						modifiedDate: undefined,
						createdBy: undefined,
						createdDate: undefined,
						programId: programFieldToAdd.programId,
						templateId: programFieldToAdd.physicalComponentToAdd,
					};
					createPhysicalComponentFieldTemplate(physicalComponentField).then(
						(response) => {
							if (response.status == 201) {
								fieldToAdd = response.data;
							}
						}
					);
					break;
			}

			if (fieldToAdd) {
				return new Promise((resolve) => {
					resolve("Successfully Added Field to Program");
				});
			} else {
				return new Promise((reject) => {
					reject("Failed to Add Field to Program");
				});
			}
			/*TODO: Hookup to backend. */
		} catch (error: any) {
			// console.log(error);
			// setErrors(error.response.data);
		}
	}

	const toggleProgram = (program: SelectProgram) => {
		// Check if the ID already exists
		const indexToRemove = programsToAdd.findIndex((p) => p.id === program.id);
		if (indexToRemove === -1) {
			setProgramsToAdd([...programsToAdd, program]);
		} else {
			setProgramsToAdd(programsToAdd.filter((p) => p.id !== program.id));
		}
	};

	const handleSubmit = () => {
		setAddingField(true);
		const promises = programsToAdd.map((program) =>
			PostAddProgramField({ ...formikProps.values, programId: program.id })
		);
		Promise.all(promises)
			.then((res) =>
				addNewAlert({
					type: "success",
					message: "Successfully added field to programs",
				})
			)
			.catch((err) =>
				addNewAlert({
					type: "error",
					message: "Failed to add field to programs",
				})
			)
			.finally(() => setAddingField(false));
	};

	useEffect(() => {
		getActiveCreditFieldTemplates()
			.then((response) => {
				setActiveCredits(response.data);
			})
			.catch((error) => {
				// console.log("getActiveCreditFieldTemplates ERROR", error);
			});

		getActiveDateFieldTemplates()
			.then((response) => {
				setActiveDates(response.data);
			})
			.catch((error) => {
				// console.log("getActiveDateFieldTemplates ERROR", error);
			});

		getActiveListFieldTemplates()
			.then((response) => {
				setActiveLists(response.data);
			})
			.catch((error) => {
				// console.log("getActiveListFieldTemplates ERROR", error);
			});

		getActiveTextFieldTemplates()
			.then((response) => {
				setActiveTexts(response.data);
			})
			.catch((error) => {
				// console.log("getActiveTextFieldTemplates ERROR", error);
			});

		getActiveLinkFieldTemplates()
			.then((response) => {
				setActiveLinks(response.data);
			})
			.catch((error) => {
				// console.log("getActiveLinkFieldTemplates ERROR", error);
			});

		getActiveMediaFieldTemplates()
			.then((response) => {
				setActiveMedia(response.data);
			})
			.catch((error) => {
				// console.log("getActiveMediaFieldTemplates ERROR", error);
			});
		getActivePhysicalComponentFieldTemplates()
			.then((response) => {
				setActivePhysicalComponents(response.data);
			})
			.catch((error) => {
				// console.log("getActivePhysicalComponentFieldTemplates ERROR", error);
			});

		// getRootProgramConfig().then((res) => {
		// 	setRootPrograms(res.data);
		// });

		getRootProgramOptions().then((res) => {
			if (res.status === 200) {
				setRootPrograms(res.data);
			}
		});
	}, []);

	return (
		<CardContainer>
			<BrowserHeader title={`Bulk Add Field Template`} />

			<CardHeader className="justify-between gap-[1rem] lg:!flex-nowrap">
				<h2 className="card-title">Bulk Add Field Template</h2>
			</CardHeader>

			<CardBody className="flex flex-col gap-[2rem]">
				<FormikProvider value={formikProps}>
					<h2>Field Type Selection</h2>
					<DropdownField
						className="max-w-[450px]"
						name="fieldType"
						placeholder="Select Field Type"
						options={FieldTemplateNames.map((a) => {
							return { value: a.value, label: a.label };
						})}
						onChange={handleFieldDropdownData}
					/>
					{fieldDropdownData == 1 && (
						<div>
							<DropdownField
								className="max-w-[450px]"
								name="creditToAdd"
								placeholder="Select Credit Field Type"
								options={activeCredits.map((a) => {
									return { value: a.id, label: a.name };
								})}
							/>
						</div>
					)}
					{fieldDropdownData == 2 && (
						<div>
							<DropdownField
								className="max-w-[450px]"
								name="dateToAdd"
								placeholder="Select Date Field Type"
								options={activeDates.map((a) => {
									return { value: a.id, label: a.name };
								})}
							/>
						</div>
					)}
					{fieldDropdownData == 3 && (
						<div>
							<DropdownField
								className="max-w-[450px]"
								name="listToAdd"
								placeholder="Select List Field Type"
								options={activeLists.map((a) => {
									return { value: a.id, label: a.name };
								})}
							/>
						</div>
					)}
					{fieldDropdownData == 4 && (
						<div>
							<DropdownField
								className="max-w-[450px]"
								name="textToAdd"
								placeholder="Select Text Field Type"
								options={activeTexts.map((a) => {
									return { value: a.id, label: a.name };
								})}
							/>
						</div>
					)}
					{fieldDropdownData == 5 && (
						<div>
							<DropdownField
								className="max-w-[450px]"
								name="linkToAdd"
								placeholder="Select Link Field Type"
								options={activeLinks.map((a) => {
									return { value: a.id, label: a.name };
								})}
							/>
						</div>
					)}
					{fieldDropdownData == 6 && (
						<div>
							<DropdownField
								className="max-w-[450px]"
								name="mediaToAdd"
								placeholder="Select Media Field Type"
								options={activeMedia.map((a) => {
									return { value: a.id, label: a.name };
								})}
							/>
						</div>
					)}
					{fieldDropdownData == 7 && (
						<div>
							<DropdownField
								className="max-w-[450px]"
								name="physicalComponentToAdd"
								placeholder="Select Physical Component Field Type"
								options={activePhysicalComponents.map((a) => {
									return { value: a.id, label: a.name };
								})}
							/>
						</div>
					)}
					<h2>Multiselect Programs</h2>
					{rootPrograms.length > 0 ? (
						<>
							<DropdownField
								className="max-w-[450px]"
								name="rootProgram"
								placeholder="Select Root Level Program"
								options={rootPrograms}
								onChange={(e) => {
									if (!e.target.value) setNestedProgram(null);

									const selectedProgram = rootPrograms.find(
										(p) => String(p.value) === e.target.value
									);

									if (selectedProgram) {
										getProgramConfigById(String(e.target.value)).then((res) => {
											if (res.status === 200) {
												setNestedProgram(res.data);
											}
										});
									} else {
										setNestedProgram(null);
									}
								}}
							/>
							<div className="flex flex-wrap gap-[3rem]">
								<div className="flex flex-col gap-[1rem] min-w-[70%] flex-1">
									{nestedProgram && (
										<ProgramLevel
											key={nestedProgram.id}
											program={nestedProgram}
											// addProgramToArr={handleAdd}
											// removeProgramFromArr={handleRemove}
											programsToAdd={programsToAdd}
											checked={programsToAdd.some(
												(p) => p.id === nestedProgram.id
											)}
											toggleProgram={toggleProgram}
										/>
									)}
								</div>

								<ModalCard
									className="flex-1 min-w-[320px]"
									title={`Selected Programs (${programsToAdd.length})`}
									iconColor={theme.colorCopyLightLight}
								>
									<div className="flex flex-col gap-[1rem] p-[1rem] flex-1 overflow-y-auto">
										{sortedPrograms.map((program) => (
											<div
												key={program.id}
												className="flex items-center gap-[1rem]"
											>
												<SquareButton
													className="button-light"
													onClick={() => toggleProgram(program)}
												>
													<Icon
														icon="close"
														color={theme.colorActivation}
														width="15px"
														height="15px"
													/>
												</SquareButton>

												<span className="text-colorCopyLight">
													{program.ancestry?.map((a, index) => {
														const lastItem =
															index === program.ancestry.length - 1;
														if (lastItem) {
															return (
																<b className="text-colorCopyDarkDark">
																	{a.name} ({program.id})
																</b>
															);
														} else {
															return a.name + " > ";
														}
													})}
												</span>
											</div>
										))}
									</div>
									<div className="flex flex-wrap justify-end gap-[1rem] p-[1rem] bg-colorBackgroundLight">
										<Button
											className="button-light w-[265px]"
											iconLeft="close"
											iconSize="18px"
											// iconColor={theme.colorCopyLightLight}
											onClick={() => setProgramsToAdd([])}
											type="button"
											disabled={programsToAdd.length === 0}
										>
											Clear Selection
										</Button>
										<Button
											className="w-[265px]"
											iconLeft="plus"
											iconColor={theme.colorCopyLightLight}
											onClick={handleSubmit}
											type="submit"
											loading={addingField}
											disabled={
												programsToAdd.length === 0 || fieldDropdownData === 0
											}
										>
											Add Field to Programs
										</Button>
									</div>
								</ModalCard>
							</div>
						</>
					) : (
						<div className="flex items-center gap-[.5rem]">
							<span>Loading Root Level Programs </span>
							<Loading
								className="!relative !top-0 !left-0 !transform-none"
								showLogo={false}
								fullScreen={false}
								size="25px"
							/>
						</div>
					)}
				</FormikProvider>
			</CardBody>
		</CardContainer>
	);
};

export default BulkAddFieldTemplates;

interface BulkAddFieldTemplatesProps {}

interface SelectProgram {
	id: number;
	name: string;
	ancestry: Ancestor[];
}
